import type { RouteProp } from '@react-navigation/native';
import { useRoute } from '@react-navigation/native';
import { AddressTypes } from '@sweetgreen/garnish';

import { useCustomer } from '@order/Customer';
import type { useOrders } from '@order/Order';

import type { MenuTabStackParamList } from '../../../../navigation/AppNavigation.props';

/**
 * Load default delivery channel info in the following order:
 *
 * 1. Previous address (from navigating from delivery menu).
 * 2. Last delivery order.
 * 3. First customer address.
 * 4. Null for not available.
 *
 * Note that any chosen address must exist in the customer's current addresses.
 */

export const useDefaultDeliveryChannel = (
  props: UseDefaultDeliveryChannelProps,
) => {
  const { orders } = props;
  const { customer } = useCustomer();

  // ─── Existing addresses validation. ─────────────────────────────

  const customerAddresses = customer?.addresses ?? [];
  const existingAddressesIds = new Set(
    customerAddresses.map((address) => address.id),
  );

  // ─── 1. Previous address (from navigating from delivery menu). ──

  const { params } = useRoute<RouteProp<MenuTabStackParamList, 'Locations'>>();
  const previousDeliveryId = params?.previousDeliveryId;
  const previousAddress = customerAddresses.find(
    ({ id }) => id === previousDeliveryId,
  );

  const doesPreviousAddressExist =
    previousAddress?.id &&
    previousAddress.name &&
    existingAddressesIds.has(previousAddress.id);

  if (doesPreviousAddressExist) return previousAddress;

  // ─── 2. Last delivery order. ────────────────────────────────────

  const deliveryOrder = orders.find(
    (order) => order.orderType === AddressTypes.delivery.toUpperCase(),
  );
  const deliveryOrderAddress = deliveryOrder?.deliveryOrderDetail?.address;

  const doesDeliveryOrderAddressExist =
    deliveryOrderAddress?.id &&
    deliveryOrderAddress.name &&
    existingAddressesIds.has(deliveryOrderAddress.id);

  if (doesDeliveryOrderAddressExist) return deliveryOrderAddress;

  // ─── 3. First customer address. ─────────────────────────────────

  const firstCustomerAddress = customerAddresses[0];

  const doesFirstCustomerAddressExist =
    firstCustomerAddress?.id &&
    firstCustomerAddress.name &&
    existingAddressesIds.has(firstCustomerAddress.id);

  if (doesFirstCustomerAddressExist) return firstCustomerAddress;

  // ─── 4. Null for not available. ─────────────────────────────────

  return null;
};

type UseDefaultDeliveryChannelProps = {
  readonly orders: ReturnType<typeof useOrders>['orders'];
};
